import { useQuery } from '@tanstack/react-query';
import { PkMatchFacts, PkSpinner } from '@uefadigital/panenka-react';
import { memo, useCallback, useMemo } from 'react';

import Event from './Event';

import { EventsFilter } from '@apis/events';
import { useWindowSize } from '@hooks/useWindowSize';
import { getMatchEventsQuery } from '@queries/matches';
import LiveIndicator from '@shared/LiveIndicator';
import useMatch from '@shared/MatchUnit/useMatch';
import { useTranslateFn } from '@shared/Translate';
import { Order } from '@shared-types/common.types';
import type { Match, PlayerEvent } from '@shared-types/match.types';
import { EventSubType, EventType, MatchStatus } from '@shared-types/match.types';
import { IS_TEST } from '@utilities/browser';
import { getRefreshRate, includeTimelineEvent, isFutsal } from '@utilities/live';
import { currentSeason } from '@utilities/WindowContext';

export type MatchTimelineOptions = {
  match: Match;
  interval?: number;
  includeVarEvents?: boolean;
};

const Timeline = ({ options }: { options: MatchTimelineOptions }) => {
  const t = useTranslateFn();
  const { isDesktop } = useWindowSize();
  const match = useMatch(options.match);

  const pollInterval = useMemo(
    () => (options.interval < 0 ? options.interval : getRefreshRate(match)),
    [options.interval, match],
  );

  const isLive = match.status === MatchStatus.LIVE;
  const isFemaleCompetition = match.competition?.sex === 'FEMALE';
  const isFutsalMatch = isFutsal(match);
  const enablePlayerLink = +match.seasonYear >= currentSeason;

  const filter = useMemo(
    () =>
      [
        EventsFilter.MAIN,
        EventsFilter.PENALTY_MISSED_REGULAR_TIME,
        options.includeVarEvents && EventsFilter.VAR,
      ].filter(Boolean),
    [options.includeVarEvents],
  );

  const { data: events, isLoading } = useQuery({
    ...getMatchEventsQuery(match.id, {
      filter,
      offset: 0,
      limit: 500,
      order: Order.DESC,
    }),
    select: (ev) => ev.filter(includeTimelineEvent(options.includeVarEvents)),
    refetchInterval: !IS_TEST && pollInterval,
    placeholderData: [],
  });

  const getHomeAwayTeam = useCallback(
    (event: PlayerEvent) => {
      if (event?.type === EventType.GOAL && event?.subType === EventSubType.OWN)
        return event?.primaryActor?.team?.id === match.awayTeam?.id ? 'home' : 'away';

      return event?.primaryActor?.team?.id === match.awayTeam?.id ? 'away' : 'home';
    },
    [match.awayTeam?.id],
  );

  if (isLoading) return <PkSpinner />;
  if (!events?.length) return <div>{t('no_events')}</div>;

  return (
    <PkMatchFacts>
      {isLive && <LiveIndicator size={isDesktop ? 'M' : 'S'} style={{ zIndex: 2 }} />}
      {events.map((event) => (
        <Event
          key={event.id}
          event={event}
          team={getHomeAwayTeam(event)}
          isFemaleCompetition={isFemaleCompetition}
          isFutsalMatch={isFutsalMatch}
          enablePlayerLink={enablePlayerLink}
          competitionId={match.competition?.id}
          phase={match.round?.phase}
        />
      ))}
    </PkMatchFacts>
  );
};

export default memo(Timeline);
